
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FrankApiConfig {
  id: string;
  organization_id: string;
  api_url: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const useFrankApiConfig = (organizationId?: string) => {
  const [config, setConfig] = useState<FrankApiConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadConfig = async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('frank_api_config')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error loading Frank API config:', error);
        throw error;
      }

      setConfig(data);
    } catch (error: any) {
      console.error('Error loading Frank API config:', error);
      toast.error('Failed to load Frank API configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async (apiUrl: string, isEnabled: boolean): Promise<boolean> => {
    if (!organizationId) return false;

    setIsLoading(true);
    try {
      const configData = {
        organization_id: organizationId,
        api_url: apiUrl,
        is_enabled: isEnabled,
        updated_at: new Date().toISOString()
      };

      if (config) {
        // Update existing config
        const { error } = await supabase
          .from('frank_api_config')
          .update(configData)
          .eq('id', config.id);

        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from('frank_api_config')
          .insert(configData);

        if (error) throw error;
      }

      await loadConfig(); // Reload to get updated data
      toast.success('Frank API configuration saved successfully');
      return true;
    } catch (error: any) {
      console.error('Error saving Frank API config:', error);
      toast.error('Failed to save Frank API configuration');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, [organizationId]);

  return {
    config,
    isLoading,
    saveConfig,
    reloadConfig: loadConfig
  };
};
